import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import Quill, { Delta } from 'quill';
import 'quill/dist/quill.bubble.css';

import {
    Bold,
    Highlighter,
    Italic,
    Link,
    Pencil,
    Search,
    Share,
    Strikethrough,
    Trash,
    Underline,
    X,
    Type,
    ALargeSmall,
    Heading1,
    Heading2,
    Heading3,
    Text,
    List,
    ListOrdered,
    CaseSensitive,
    Mic,
    AudioLines,
    Send,
    ArrowUp,
    PictureInPicture2,
    PanelRight,
    SquarePen,
    FileDown,
    Sparkles,
    Check,
    AlignLeft,
    AlignCenter,
    AlignRight,
    AlignJustify,
    EllipsisVertical,
    FilePlus,
    Plus,
    Upload,
    ArrowLeft
} from "lucide-react";

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuPortal,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

import { AnimatePresence, motion } from 'framer-motion';

import { Separator } from "@/components/ui/separator"

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"

import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover"


import { NotesProvider, useNotes } from "../contexts/NotesContext"
import { Toggle } from '@/components/ui/toggle';
import { useWidget } from '../lib/function-signatures';

export const EditorToolbar = ({ setActiveTab }: { setActiveTab?: (tab: string) => void }) => {

    const { quillEditor, isWidget } = useNotes();

    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isUnderline, setIsUnderline] = useState(false);
    const [isStrikethrough, setIsStrikethrough] = useState(false);
    const [textFormat, setTextFormat] = useState("text");
    const [textColor, setTextColor] = useState("default");
    const [textAlign, setTextAlign] = useState("left");
    const [isHighlight, setIsHighlight] = useState(false);
    const [highlightColor, setHighlightColor] = useState("#fff87599");
    const [isMore, setIsMore] = useState(false);
    const [fileLinkingOpen, setFileLinkingOpen] = useState(false);
    const [linkType, setLinkType] = useState("assignments");
    const [linkSearchResults, setLinkSearchResults] = useState<any[]>([]);

    // Store the last known selection to restore it when toolbar buttons are clicked
    const lastSelectionRef = useRef<{ index: number; length: number } | null>(null);

    const toggleMore = () => {
        setIsMore(!isMore);
    };

    // Helper function to maintain focus and selection when applying formatting
    const withFocusAndSelection = (range: { index: number; length: number } | null, callback: () => void) => {
        if (!quillEditor) return;

        // Execute the formatting callback
        callback();


        // Restore the selection after formatting
        if (range) {
            quillEditor.setSelection(range.index, range.length);
            quillEditor.focus();
        }

    };

    const getFormatIcon = () => {
        switch (textFormat) {
            case "title":
                return <Heading1 className="min-w-[20px] min-h-[20px] text-theme-0" />;
            case "heading":
                return <Heading2 className="min-w-[20px] min-h-[20px] text-theme-0" />;
            case "subheading":
                return <Heading3 className="min-w-[20px] min-h-[20px] text-theme-0" />;
            case "bullet":
                return <List className="min-w-[20px] min-h-[20px] text-theme-0" />;
            case "numbered":
                return <ListOrdered className="min-w-[20px] min-h-[20px] text-theme-0" />;
            default:
                return <CaseSensitive className="min-w-[20px] min-h-[20px] text-theme-0" />;
        }
    };

    useEffect(() => {
        // Function to update format states based on current selection
        const updateFormatStates = () => {
            if (quillEditor) {
                const format = quillEditor.getFormat();
                setIsBold(Boolean(format.bold));
                setIsItalic(Boolean(format.italic));
                setIsUnderline(Boolean(format.underline));
                setIsStrikethrough(Boolean(format.strike));
                setTextAlign(format.align || "left");
                setTextColor(format.color || "default");

                // Detect background color without overwriting chosen color when none is present
                const bg: string | undefined = (format['background-color'] || format['background']) as any;
                if (bg) {
                    const normalized = rgbaToHex(bg);
                    setHighlightColor(normalized);
                    setIsHighlight(true);
                } else {
                    setIsHighlight(false);
                }

                // Update text format state
                if (format.header === 1) setTextFormat("title");
                else if (format.header === 2) setTextFormat("heading");
                else if (format.header === 3) setTextFormat("subheading");
                else if (format.list === "bullet") setTextFormat("bullet");
                else if (format.list === "ordered") setTextFormat("numbered");
                else setTextFormat("text");
            }
        };

        // Listen for custom selection events from shadow DOM
        const handleCustomSelectionChange = (event: CustomEvent) => {
            const { range, quill } = event.detail;
            console.log("✅ TOOLBAR RECEIVED SELECTION:", range);

            if (range !== null) {
                // Store the selection for toolbar button use
                lastSelectionRef.current = { index: range.index, length: range.length };
                updateFormatStates();
            }
        };

        // Add custom selection event listener to document (events bubble up from shadow DOM)
        document.addEventListener('quill-selection-change', handleCustomSelectionChange as EventListener);

        if (quillEditor) {
            // Fallback: Try standard Quill events (may not work in shadow DOM)
            quillEditor.on('selection-change', (range: { index: number; length: number; } | null) => {
                console.log("🔄 selection-change", range);
                if (range !== null) {
                    console.log(quillEditor.getFormat(range.index));
                    lastSelectionRef.current = { index: range.index, length: range.length };
                    updateFormatStates();
                }
            });

            // Add text-change handler to update format states
            quillEditor.on('text-change', () => {
                updateFormatStates();
            });


        }

        // Cleanup function
        return () => {
            document.removeEventListener('quill-selection-change', handleCustomSelectionChange as EventListener);
        };
    }, [quillEditor]);

    const handleFormatChange = (value: string) => {
        withFocusAndSelection(lastSelectionRef.current, () => {
            if (!quillEditor) return;

            // Get the current cursor position
            const currentIndex = lastSelectionRef.current?.index || 0;

            // Find the current line at cursor position
            const [line] = quillEditor.getLine(currentIndex);
            if (line) {
                const lineIndex = quillEditor.getIndex(line);
                const lineLength = line.length();

                // Remove existing formatting from the entire line
                quillEditor.formatText(lineIndex, lineLength, "header", false);
                quillEditor.formatText(lineIndex, lineLength, "list", false);

                // Apply new format to the entire line
                switch (value) {
                    case "title":
                        quillEditor.formatText(lineIndex, lineLength, "header", 1);
                        break;
                    case "heading":
                        quillEditor.formatText(lineIndex, lineLength, "header", 2);
                        break;
                    case "subheading":
                        quillEditor.formatText(lineIndex, lineLength, "header", 3);
                        break;
                    case "bullet":
                        quillEditor.formatText(lineIndex, lineLength, "list", "bullet");
                        break;
                    case "numbered":
                        quillEditor.formatText(lineIndex, lineLength, "list", "ordered");
                        break;
                    default:
                        // Text format - remove any existing formatting
                        break;
                }
            }

            setTextFormat(value);
        });
    };

    const handleColorChange = (value: string) => {
        withFocusAndSelection(lastSelectionRef.current, () => {
            if (!quillEditor) return;

            if (value === "default") {
                quillEditor.format('color', false);
            } else {
                quillEditor.format('color', value);
            }
            setTextColor(value);
        });
    };

    const handleHighlightColorChange = (value: string) => {
        withFocusAndSelection(lastSelectionRef.current, () => {
            if (!quillEditor) return;

            console.log("changed highlight to ", value);
            quillEditor.format('background', value);
            setHighlightColor(value);
            setIsHighlight(true);
        });
    };

    const handleAlignChange = (value: string) => {
        withFocusAndSelection(lastSelectionRef.current, () => {
            if (!quillEditor) return;

            if (value === "left") {
                quillEditor.format('align', false);
            } else {
                quillEditor.format('align', value);
            }
            setTextAlign(value);
        });
    };

    const toggleBold = () => {
        withFocusAndSelection(lastSelectionRef.current, () => {
            if (!quillEditor) return;

            const format = quillEditor.getFormat();
            quillEditor.format('bold', !format.bold);
            setIsBold(!format.bold);
        });
    };

    const toggleItalic = () => {
        withFocusAndSelection(lastSelectionRef.current, () => {
            if (!quillEditor) return;

            const format = quillEditor.getFormat();
            quillEditor.format('italic', !format.italic);
            setIsItalic(!format.italic);
        });
    };

    const toggleUnderline = () => {
        withFocusAndSelection(lastSelectionRef.current, () => {
            if (!quillEditor) return;

            const format = quillEditor.getFormat();
            quillEditor.format('underline', !format.underline);
            setIsUnderline(!format.underline);
        });
    };



    const rgbaToHex = (rgba: string) => {
        // Handle rgba format
        const rgbaMatch = rgba.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d*\.?\d+))?\)$/);
        if (rgbaMatch) {
            const [_, r, g, b, a] = rgbaMatch;
            const hex = '#' +
                ('0' + parseInt(r).toString(16)).slice(-2) +
                ('0' + parseInt(g).toString(16)).slice(-2) +
                ('0' + parseInt(b).toString(16)).slice(-2);

            // If alpha channel exists and is not 1, append it
            if (a && parseFloat(a) !== 1) {
                const alpha = Math.round(parseFloat(a) * 255).toString(16);
                return hex + ('0' + alpha).slice(-2);
            }
            return hex;
        }
        // If it's already a hex or other format, return as is
        return rgba;
    }

    const toggleHighlight = () => {
        withFocusAndSelection(lastSelectionRef.current, () => {
            if (!quillEditor) return;

            const format = quillEditor.getFormat();
            const currentBg = (format["background-color"] || format['background'] || "") as string;
            const normalizedCurrent = rgbaToHex(currentBg);
            const isCurrentlyHighlighted = normalizedCurrent === highlightColor;
            console.log("isCurrentlyHighlighted", isCurrentlyHighlighted, format);

            if (isCurrentlyHighlighted && (lastSelectionRef.current?.length ?? 0) > 0) {
                // Remove highlight
                quillEditor.format('background', false);
                setIsHighlight(false);
            } else {
                // Apply highlight (use selected color or default yellow)
                const colorToApply = highlightColor || '#fff87599';
                quillEditor.format('background', colorToApply);
                setHighlightColor(colorToApply);
                setIsHighlight(true);
            }
        });
    };

    const { switchNote, settings, portal } = useNotes();


    const { handleDeleteNote } = useNotes();

    const [confirmDelete, setConfirmDelete] = useState(false);

    const searchAssignments = () => {
        const data: any[] = [];

        setLinkSearchResults(data);

    }

    const insertAssignment = (assignment: any) => {
        if (quillEditor) {
            const range = quillEditor.getSelection(true);
            // Insert the assignment embed
            quillEditor.insertEmbed(range.index, 'assignment', {
                id: assignment._id,
                title: assignment.title
            });
            // Move cursor to next line
            quillEditor.insertText(range.index + 1, '\n');
            quillEditor.setSelection(range.index + 2, 0);
            setFileLinkingOpen(false);
        }
    };

    return (
        <div className="flex items-center justify-between px-[20px] border-b border-theme h-[60px]">
            <div className="flex items-center">
                {setActiveTab && (
                    <Toggle pressed={false} onClick={() => setActiveTab("navigation")} className="flex items-center text-theme-1 min-h-[20px] mr-[5px]">
                        <ArrowLeft className="min-w-[18px] min-h-[18px] text-theme-1 hover:text-theme-1" />
                    </Toggle>
                )}
                <Toggle pressed={confirmDelete} onClick={() => setConfirmDelete((prev: boolean) => !prev)} className="flex items-center text-theme-1 min-h-[20px] mr-[5px]">
                    <Trash className={`${confirmDelete ? "text-red-500" : "text-theme-1"} min-w-[18px] min-h-[18px]`} />
                </Toggle>
                <AnimatePresence>
                    {confirmDelete && (
                        <motion.div
                            initial={{ width: 0, opacity: 0 }}
                            animate={{ width: "auto", opacity: 1 }}
                            exit={{ width: 0, opacity: 0 }}
                            className="flex items-center gap-[5px] overflow-hidden"
                        >
                            <Toggle
                                onClick={(e: any) => {
                                    e.stopPropagation();
                                    // TODO: handle delete
                                    handleDeleteNote();
                                    setConfirmDelete(false);
                                }}
                            >
                                <Check className="min-w-[18px] min-h-[18px] text-theme-1 hover:text-theme-1" />
                            </Toggle>
                            <Toggle
                                onClick={(e: any) => {
                                    e.stopPropagation();
                                    setConfirmDelete(false);
                                }}
                            >
                                <X className="min-w-[18px] min-h-[18px] text-theme-1 hover:text-theme-1" />
                            </Toggle>
                        </motion.div>
                    )}
                </AnimatePresence>
                {/*}
                <Separator orientation="vertical" className="!h-[20px] mx-[10px]" />
                
                <Toggle pressed={false}>
                    <FileDown className="min-w-[18px] min-h-[18px] text-theme-1 hover:text-theme-1" />
                </Toggle>
                */}
            </div>
            <div className="flex items-center gap-[2px]">
                <Select value={textFormat} onValueChange={handleFormatChange}>
                    <SelectTrigger withArrow={false} className="cursor-pointer border-0 outline-none shadow-none hover:bg-theme-2 transition-all duration-200 rounded-md px-[8px] py-0">
                        {getFormatIcon()}
                    </SelectTrigger>
                    <SelectContent container={portal}>
                        <SelectItem value="title" className="flex items-center gap-[15px]">
                            <Heading1 className="w-[20px] h-[20px]" />
                            <p className="font-bold text-xl">Title</p>
                        </SelectItem>
                        <SelectItem value="heading" className="flex items-center gap-[15px]">
                            <Heading2 className="w-[20px] h-[20px]" />
                            <p className="font-semibold text-lg">Header</p>
                        </SelectItem>
                        <SelectItem value="subheading" className="flex items-center gap-[15px]">
                            <Heading3 className="w-[20px] h-[20px]" />
                            <p className="font-semibold text-base">Subheader</p>
                        </SelectItem>
                        <SelectItem value="text" className="flex items-center gap-[15px]">
                            <CaseSensitive className="w-[20px] h-[20px]" />
                            <p className="font-normal text-sm">Text</p>
                        </SelectItem>
                        <SelectItem value="bullet" className="flex items-center gap-[15px]">
                            <List className="w-[20px] h-[20px]" />
                            <p className="font-normal text-sm">Bullet list</p>
                        </SelectItem>
                        <SelectItem value="numbered" className="flex items-center gap-[15px]">
                            <ListOrdered className="w-[20px] h-[20px]" />
                            <p className="font-normal text-sm">Number list</p>
                        </SelectItem>
                    </SelectContent>
                </Select>
                <Toggle
                    aria-label="Toggle bold"
                    pressed={isBold}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        toggleBold();
                    }}
                    className="text-theme-1 hover:text-theme-1"
                >
                    <Bold className="h-[20px] w-[20px]" />
                </Toggle>
                <Toggle
                    aria-label="Toggle italic"
                    pressed={isItalic}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        toggleItalic();
                    }}
                    className="text-theme-1 hover:text-theme-1"
                >
                    <Italic className="h-[20px] w-[20px]" />
                </Toggle>
                <Toggle
                    aria-label="Toggle underline"
                    pressed={isUnderline}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        toggleUnderline();
                    }}
                    className="text-theme-1 hover:text-theme-1"
                >
                    <Underline className="h-[20px] w-[20px]" />
                </Toggle>
                <div className="flex items-center gap-[2px]">
                    <Toggle
                        aria-label="Toggle highlight"
                        pressed={isHighlight}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            toggleHighlight();
                        }}
                        className="text-theme-1 hover:text-theme-1 data-[state=on]:bg-transparent"
                        style={isHighlight ? { backgroundColor: highlightColor } : {}}
                    >
                        <Highlighter className="h-[20px] w-[20px]" />
                    </Toggle>
                    <Select value={highlightColor} onValueChange={handleHighlightColorChange}>
                        <SelectTrigger className="w-fit px-2 border-0 outline-none shadow-none relative pl-0">
                        </SelectTrigger>
                        <SelectContent container={portal}>
                            <SelectItem value="#fff87599" className="flex items-center gap-[15px]">
                                <Highlighter className="w-[20px] h-[20px]" />
                                <p className="font-normal text-sm" style={{ backgroundColor: "#fff87599" }}>Yellow</p>
                            </SelectItem>
                            <SelectItem value="#9fffc099" className="flex items-center gap-[15px]">
                                <Highlighter className="w-[20px] h-[20px]" />
                                <p className="font-normal text-sm" style={{ backgroundColor: "#9fffc099" }}>Green</p>
                            </SelectItem>
                            <SelectItem value="#b2e3ff99" className="flex items-center gap-[15px]">
                                <Highlighter className="w-[20px] h-[20px]" />
                                <p className="font-normal text-sm" style={{ backgroundColor: "#b2e3ff99" }}>Blue</p>
                            </SelectItem>
                            <SelectItem value="#d3aaff99" className="flex items-center gap-[15px]">
                                <Highlighter className="w-[20px] h-[20px]" />
                                <p className="font-normal text-sm" style={{ backgroundColor: "#d3aaff99" }}>Purple</p>
                            </SelectItem>
                            <SelectItem value="#ff9adc99" className="flex items-center gap-[15px]">
                                <Highlighter className="w-[20px] h-[20px]" />
                                <p className="font-normal text-sm" style={{ backgroundColor: "#ff9adc99" }}>Pink</p>
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <Popover open={isMore} onOpenChange={setIsMore}>
                    <PopoverTrigger>
                        <Toggle
                            aria-label="Toggle more"
                            pressed={isMore}
                            onClick={toggleMore}
                            className="text-theme-1 hover:text-theme-1"
                        >
                            <EllipsisVertical className="w-[20px] h-[20px]" />
                        </Toggle>
                    </PopoverTrigger>
                    <PopoverContent className="flex items-center gap-[10px] px-[10px] py-[8px]" container={portal}>
                        <Select value={textColor} onValueChange={handleColorChange}>
                            <SelectTrigger className="w-fit px-[8px] py-[6px]">
                                <Type className="w-[20px] h-[20px] text-theme-0" style={textColor === "default" ? undefined : { color: textColor }} />
                            </SelectTrigger>
                            <SelectContent container={portal}>
                                <SelectItem value="default" className="flex items-center gap-[15px]">
                                    <Type className="w-[20px] h-[20px] text-theme-0" />
                                    <p className="font-normal text-sm">Default</p>
                                </SelectItem>
                                <SelectItem value="#ef4444" className="flex items-center gap-[15px]">
                                    <Type className="w-[20px] h-[20px] text-[#ef4444]" />
                                    <p className="font-normal text-sm text-[#ef4444]">Red</p>
                                </SelectItem>
                                <SelectItem value="#22c55e" className="flex items-center gap-[15px]">
                                    <Type className="w-[20px] h-[20px] text-[#22c55e]" />
                                    <p className="font-normal text-sm text-[#22c55e]">Green</p>
                                </SelectItem>
                                <SelectItem value="#3b82f6" className="flex items-center gap-[15px]">
                                    <Type className="w-[20px] h-[20px] text-[#3b82f6]" />
                                    <p className="font-normal text-sm text-[#3b82f6]">Blue</p>
                                </SelectItem>
                                <SelectItem value="#eab308" className="flex items-center gap-[15px]">
                                    <Type className="w-[20px] h-[20px] text-[#eab308]" />
                                    <p className="font-normal text-sm text-[#eab308]">Yellow</p>
                                </SelectItem>
                                <SelectItem value="#a855f7" className="flex items-center gap-[15px]">
                                    <Type className="w-[20px] h-[20px] text-[#a855f7]" />
                                    <p className="font-normal text-sm text-[#a855f7]">Purple</p>
                                </SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={textAlign} onValueChange={handleAlignChange}>
                            <SelectTrigger className="w-fit px-[8px] py-[6px]">
                                <AlignLeft className="w-[20px] h-[20px] text-theme-0" />
                            </SelectTrigger>
                            <SelectContent container={portal}>
                                <SelectItem value="left" className="flex items-center gap-[15px]">
                                    <AlignLeft className="w-[20px] h-[20px] text-theme-0" />
                                    <p className="font-normal text-sm">Left</p>
                                </SelectItem>
                                <SelectItem value="center" className="flex items-center gap-[15px]">
                                    <AlignCenter className="w-[20px] h-[20px] text-theme-0" />
                                    <p className="font-normal text-sm">Center</p>
                                </SelectItem>
                                <SelectItem value="right" className="flex items-center gap-[15px]">
                                    <AlignRight className="w-[20px] h-[20px] text-theme-0" />
                                    <p className="font-normal text-sm">Right</p>
                                </SelectItem>
                                <SelectItem value="justify" className="flex items-center gap-[15px]">
                                    <AlignJustify className="w-[20px] h-[20px] text-theme-0" />
                                    <p className="font-normal text-sm">Justify</p>
                                </SelectItem>
                            </SelectContent>
                        </Select>
                        <button className="p-[6px]" onClick={() => {
                            const url = prompt('Enter link URL:');
                            if (url) {
                                const range = quillEditor?.getSelection();
                                if (range) {
                                    quillEditor?.format('link', url);
                                }
                            }
                        }}>
                            <Link className="w-[20px] h-[20px]" />
                        </button>
                        <DropdownMenu open={fileLinkingOpen} onOpenChange={setFileLinkingOpen}>
                            <DropdownMenuTrigger asChild>
                                <button className="p-[6px]">
                                    <FilePlus className="w-[20px] h-[20px]" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                                <DropdownMenuLabel>Link resources</DropdownMenuLabel>
                                <DropdownMenuSub>
                                    <DropdownMenuSubTrigger>Tasks</DropdownMenuSubTrigger>
                                    <DropdownMenuPortal>
                                        <DropdownMenuSubContent>
                                            <input type="text" placeholder="Search for assignment" className="w-full border-0 outline-none px-[10px] py-[5px] rounded-lg" />
                                            <div className="flex flex-col">
                                                {linkSearchResults.map((result) => (
                                                    <div key={result._id} className="flex items-center gap-[10px] px-[10px] py-[5px]">
                                                        <p className="text-sm text-theme-0">{result.title}</p>
                                                        <button onClick={() => insertAssignment(result)}>
                                                            <Plus className="w-[16px] h-[16px]" />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </DropdownMenuSubContent>
                                    </DropdownMenuPortal>
                                </DropdownMenuSub>
                                <DropdownMenuSub>
                                    <DropdownMenuSubTrigger>Files</DropdownMenuSubTrigger>
                                    <DropdownMenuPortal>
                                        <DropdownMenuSubContent>
                                            <input type="text" placeholder="Search for file" className="w-full border-0 outline-none px-[10px] py-[5px] rounded-lg" onChange={() => searchAssignments()} />
                                            <Separator className="" />
                                            <div className="flex flex-col">
                                                {linkSearchResults.map((result) => (
                                                    <button onClick={() => insertAssignment(result)} key={result._id} className="hover:bg-gray-100 rounded-lg p-[3px] flex items-center gap-[10px] px-[10px] py-[5px] justify-between">
                                                        <p className="text-sm text-theme-0">{result.title}</p>
                                                    </button>
                                                ))}
                                            </div>
                                        </DropdownMenuSubContent>
                                    </DropdownMenuPortal>
                                </DropdownMenuSub>
                                <DropdownMenuItem>
                                    <Upload className="w-[16px] h-[16px]" />
                                    Upload
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>


                    </PopoverContent>
                </Popover>
            </div>
            <div>
            {isWidget && (
                <WidgetExitButton />
            )}
            </div>
        </div>
    )
}


const WidgetExitButton = () => {

    const { closeWidget } = useWidget();
    return (
        <button onClick={() => closeWidget("stickyNote")} className="hover:cursor-pointer hover:scale-110 transition-all duration-200">
            <X className="size-4" />
        </button>
    )
}